import { useState, useEffect, createContext, useContext, ReactNode } from 'react';
import { User, Session } from '@supabase/supabase-js';
import { supabase } from '@/integrations/supabase/client';

interface AuthContextType {
  user: User | null;
  session: Session | null;
  signIn: (email: string, password: string) => Promise<{ error: any }>;
  signUp: (email: string, password: string, fullName: string) => Promise<{ error: any }>;
  signOut: () => Promise<void>;
  resetPassword: (email: string) => Promise<{ error: any }>;
  loading: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export function AuthProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Set up auth state listener
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, session) => {
        console.log('🔐 Auth event:', event, 'Email:', session?.user?.email);
        
        // VERIFICAÇÃO RIGOROSA: Detectar auto-confirmações suspeitas
        if (session?.user) {
          const createdAt = new Date(session.user.created_at);
          const confirmedAt = session.user.email_confirmed_at 
            ? new Date(session.user.email_confirmed_at) 
            : null;
          
          // Se foi criado há menos de 5 minutos e já está confirmado
          const recentlyCreated = (Date.now() - createdAt.getTime()) < 5 * 60 * 1000;
          const autoConfirmed = confirmedAt && 
            Math.abs(confirmedAt.getTime() - createdAt.getTime()) < 1000;
          
          if (recentlyCreated && autoConfirmed) {
            console.warn('⚠️ ALERTA: Possível auto-confirmação detectada!');
            console.warn('Created:', createdAt.toISOString());
            console.warn('Confirmed:', confirmedAt?.toISOString());
            console.warn('Diferença (ms):', confirmedAt ? confirmedAt.getTime() - createdAt.getTime() : 'N/A');
          }
        }
        
        setSession(session);
        setUser(session?.user ?? null);
        
        // BLOQUEIO: Se o evento for SIGNED_IN e o email não estiver confirmado
        if (event === 'SIGNED_IN' && session?.user && !session.user.email_confirmed_at) {
          console.log('🚫 Email não confirmado, bloqueando acesso');
          await supabase.auth.signOut();
          return;
        }
        
        // Log de confirmação bem-sucedida
        if (event === 'SIGNED_IN' && session?.user?.email_confirmed_at) {
          console.log('✅ Login autorizado - Email confirmado em:', session.user.email_confirmed_at);
        }
        
        setLoading(false);
      }
    );

    // Check for existing session
    supabase.auth.getSession().then(({ data: { session } }) => {
      // Verificar email confirmado na sessão existente
      if (session?.user && !session.user.email_confirmed_at) {
        console.log('🚫 Sessão existente com email não confirmado, limpando');
        supabase.auth.signOut();
        setSession(null);
        setUser(null);
      } else {
        setSession(session);
        setUser(session?.user ?? null);
      }
      setLoading(false);
    });

    return () => subscription.unsubscribe();
  }, []);

  const signIn = async (email: string, password: string) => {
    const { data, error } = await supabase.auth.signInWithPassword({
      email,
      password,
    });
    
    // Verificar se o email foi confirmado
    if (data.user && !data.user.email_confirmed_at) {
      await supabase.auth.signOut();
      return { error: { message: 'Por favor, confirme seu e-mail antes de fazer login. Verifique sua caixa de entrada.' } };
    }
    
    return { error };
  };

  const signUp = async (email: string, password: string, fullName: string) => {
    // Usar a URL de produção configurada
    const baseUrl = window.location.hostname === 'localhost' 
      ? window.location.origin 
      : 'https://ateris.com.br';
    const redirectUrl = `${baseUrl}/auth?verified=true`;
    
    console.log('📝 Iniciando cadastro...');
    console.log('🔗 Signup redirect URL:', redirectUrl);
    console.log('📧 Email:', email);
    console.log('👤 Nome:', fullName);
    
    const { data, error } = await supabase.auth.signUp({
      email,
      password,
      options: {
        emailRedirectTo: redirectUrl,
        data: {
          full_name: fullName,
        },
      },
    });
    
    if (data.user) {
      console.log('✅ Usuário criado:', data.user.id);
      console.log('📧 Email confirmado?', data.user.email_confirmed_at ? 'SIM ⚠️' : 'NÃO (esperado)');
      console.log('🕐 Criado em:', data.user.created_at);
      
      // ALERTA se email já estiver confirmado (não deveria estar)
      if (data.user.email_confirmed_at) {
        console.error('🚨 PROBLEMA: Email já confirmado imediatamente após signup!');
        console.error('🚨 Isso indica que "Confirm email" está DESABILITADO no Supabase!');
        console.error('🚨 Acesse: Authentication > Email Templates > Enable "Confirm signup"');
      }
    }
    
    return { error };
  };

  const signOut = async () => {
    try {
      console.log('🔄 Fazendo logout...');
      
      // Sempre limpar estado local primeiro
      setSession(null);
      setUser(null);
      
      // Tentar logout no Supabase, mas não falhar se der erro
      const { error } = await supabase.auth.signOut();
      
      if (error) {
        console.warn('⚠️ Aviso no logout do Supabase (continuando):', error);
        // Não fazer throw do erro - continuar com o logout local
      } else {
        console.log('✅ Logout do Supabase realizado com sucesso');
      }
      
      // Limpar localStorage como garantia adicional
      localStorage.removeItem('sb-azyixwxndwhaiimqybmd-auth-token');
      
      console.log('✅ Logout local realizado com sucesso');
      
      // Force redirect to auth page after logout
      window.location.href = '/auth';
    } catch (error) {
      console.error('❌ Erro durante logout:', error);
      
      // Mesmo com erro, limpar estado local e redirecionar
      setSession(null);
      setUser(null);
      localStorage.removeItem('sb-azyixwxndwhaiimqybmd-auth-token');
      
      console.log('✅ Logout forçado realizado - redirecionando');
      window.location.href = '/auth';
    }
  };

  const resetPassword = async (email: string) => {
    // Usar a URL de produção configurada
    const baseUrl = window.location.hostname === 'localhost' 
      ? window.location.origin 
      : 'https://ateris.com.br';
    const redirectUrl = `${baseUrl}/reset-password`;
    
    console.log('🔗 Reset password redirect URL configurado:', redirectUrl);
    console.log('🔗 Current hostname:', window.location.hostname);
    console.log('🔗 Current origin:', window.location.origin);
    
    // Tentar com configurações mais específicas para garantir que os tokens sejam incluídos
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: redirectUrl,
      captchaToken: undefined
    });
    
    if (error) {
      console.error('❌ Erro ao enviar email de recuperação:', error);
    } else {
      console.log('✅ Email de recuperação enviado com sucesso');
    }
    
    return { error };
  };

  const value = {
    user,
    session,
    signIn,
    signUp,
    signOut,
    resetPassword,
    loading,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

export function useAuth() {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}